import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { clearSharedCustomerOrganizations, prefetchCustomerSession } from '@/lib/session/sessionPrefetch';
import { loginUser, logoutUser } from '@/lib/firebase/auth';
import { findUserByPhone } from '@/lib/firebase/auth/user';
import type { UserRole } from '@/types';
import type { UserData } from '@/lib/firebase/auth/types';
import { toast } from 'sonner';
import { auth } from '@/lib/firebase/config';
import { getUserData } from '@/lib/firebase/auth/user';

interface AuthContextType {
  user: any | null;
  userData: UserData | null;
  loading: boolean;
  login: (emailOrPhone: string, password: string) => Promise<void>;
  loginWithWhatsAppOTP: (phoneNumber: string) => Promise<void>;
  logout: () => Promise<void>;
  setUserData: (data: UserData) => void;
  currentUser: FirebaseUser | null;
  hasRole: (role: UserRole) => boolean;
  switchRole: (role: UserRole) => Promise<void>;
  navigate: (path: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // During development, HMR might cause temporary context loss
    // Return a fallback object to prevent crashes
    if (process.env.NODE_ENV === 'development') {
      console.warn('AuthContext temporarily unavailable during HMR, using fallback');
      return {
        user: null,
        userData: null,
        loading: true,
        login: async () => {},
        loginWithWhatsAppOTP: async () => {},
        logout: async () => {},
        setUserData: () => {},
        currentUser: null,
        hasRole: () => false,
        switchRole: async () => {},
        navigate: () => {}
      } as AuthContextType;
    }
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Helper function to check if a route is public
const isPublicRoute = (pathname: string): boolean => {
  const publicRoutes = ['/login', '/signup', '/', '/unauthorized', '/landing', '/delete-account', '/request-treatment', '/q'];
  return publicRoutes.includes(pathname) || pathname.startsWith('/new/') || pathname.startsWith('/video-call/') || pathname.startsWith('/landing/') || pathname.startsWith('/clinic/') || pathname.startsWith('/group/');
};

interface AuthProviderProps {
  children: React.ReactNode;
  navigate: (path: string) => void;
  location: { pathname: string };
}

export function AuthProvider({ children, navigate, location }: AuthProviderProps) {
  const [user, setUser] = useState<any | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);

  // Pre-warm the Authentica settings cache as soon as the app mounts so the
  // first OTP send doesn't race Firestore's cold connection. This populates
  // both the in-memory and localStorage caches.
  useEffect(() => {
    import('@/lib/services/authentica').then(({ getAuthenticaSettings }) => {
      getAuthenticaSettings().catch(() => {});
    });
  }, []);

  // The auth listener is registered ONCE. It used to depend on
  // [navigate, location], so every page change re-registered it, Firebase
  // re-fired it and users/{uid} was read again (and every consumer re-rendered
  // with a new userData). The current route is read from refs instead, and the
  // redirects that ran on navigation are kept by the route effect below.
  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);
  const userDataRef = useRef(userData);
  navigateRef.current = navigate;
  locationRef.current = location;
  userDataRef.current = userData;

  // Signed in on /login, /signup or /: the stored redirect, else the
  // dashboard of the highest-priority active role.
  const redirectSignedInFromPublic = (data: any): boolean => {
    const publicRoutesForRedirect = ['/login', '/signup', '/'];
    console.log('[Auth State] Firebase user logged in, checking redirect. Path:', locationRef.current.pathname);
    if (!publicRoutesForRedirect.includes(locationRef.current.pathname) || !data) return false;
    // Check if there's a stored redirect URL
    const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
    console.log('[Auth State] Checking redirect URL:', redirectUrl);
    if (redirectUrl) {
      console.log('[Auth State] Found redirect URL, navigating to:', redirectUrl);
      sessionStorage.removeItem('redirectAfterLogin');
      navigateRef.current(redirectUrl);
      return true;
    }
    console.log('[Auth State] No redirect URL, going to dashboard based on role');

    // Get active roles
    const activeRoles = (data.roles || [])
      .filter((r: any) => r.isActive)
      .map((r: any) => r.role);

    // Navigate to the dashboard of their highest priority role
    const priorityOrder: UserRole[] = ['admin', 'examiner', 'manager', 'coordinator', 'marketer', 'audit', 'partner', 'volunteer', 'coach', 'customer'];
    const highestRole = priorityOrder.find(role => activeRoles.includes(role));
    if (highestRole) {
      const routePath = highestRole === 'coach' ? 'doctor' : highestRole;
      navigateRef.current(`/dashboard/${routePath}`);
      return true;
    }
    return false;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // If we already have userData (from OTP login), don't override it
      if (userDataRef.current && !user) {
        return;
      }
      
      setUser(user);
      if (user) {
        // Start the route guard's organizations + permissions reads now, in
        // parallel with the users/{uid} read below (they only need the uid).
        prefetchCustomerSession(user.uid);
        try {
          const data = await getUserData(user.uid);
          setUserData(data);
          setCurrentUser(user);
          redirectSignedInFromPublic(data);
        } catch (error) {
          setUserData(null);
          setCurrentUser(null);
          
          // Only redirect to login if not on a public route
          if (!isPublicRoute(locationRef.current.pathname)) {
            navigateRef.current('/login');
          }
        }
      } else {
        clearSharedCustomerOrganizations();
        setUserData(null);
        setCurrentUser(null);
        
        // Only redirect to login if not on a public route
        if (!isPublicRoute(locationRef.current.pathname)) {
          navigateRef.current('/login');
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On navigation, the same redirects the re-registered listener used to make,
  // from the state already loaded — no reads.
  useEffect(() => {
    if (loading) return;
    // A Firebase session only, as before: the listener skipped OTP sessions.
    if (auth.currentUser && userData) {
      redirectSignedInFromPublic(userData);
    } else if (!userData && !isPublicRoute(location.pathname)) {
      navigate('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const hasRole = (role: UserRole): boolean => {
    return userData?.roles?.some(r => r.role === role && r.isActive) || false;
  };

  const switchRole = async (role: UserRole) => {
    if (!currentUser || !userData) return;
    
    // Verify user has this role, or allow admin users to access coach dashboard
    const hasRole = userData.roles.some((r: any) => r.role === role && r.isActive);
    const isAdmin = userData.roles.some((r: any) => r.role === 'admin' && r.isActive);
    
    if (!hasRole && !(isAdmin && role === 'coach')) {
      toast.error('You do not have access to this role');
      return;
    }

    // Navigate to appropriate dashboard
    const routePath = role === 'coach' ? 'doctor' : role;
    navigate(`/dashboard/${routePath}`);
  };

  const login = async (emailOrPhone: string, password: string) => {
    try {
      const { user, userData } = await loginUser(emailOrPhone, password);
      setUser(user);
      setUserData(userData);
      setCurrentUser(user);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const loginWithWhatsAppOTP = async (phoneNumber: string) => {
    try {
      // Find user by phone number
      const userData = await findUserByPhone(phoneNumber);
      
      if (!userData) {
        throw new Error('No user found with this phone number');
      }

      // Set user data without Firebase Auth (OTP-based authentication)
      setUserData(userData);
      setUser({ uid: userData.id }); // Create a minimal user object
      
      // Create a dummy Firebase user to prevent auth state listener from redirecting
      const dummyFirebaseUser = {
        uid: userData.id,
        email: userData.email,
        displayName: `${userData.firstNameEn} ${userData.lastNameEn}`,
        photoURL: userData.photoURL,
        emailVerified: true,
        isAnonymous: false,
        metadata: {},
        providerData: [],
        refreshToken: '',
        tenantId: null,
        delete: () => Promise.resolve(),
        getIdToken: () => Promise.resolve(''),
        getIdTokenResult: () => Promise.resolve({} as any),
        reload: () => Promise.resolve(),
        toJSON: () => ({}),
        phoneNumber: userData.phone,
        providerId: 'otp'
      } as any;
      
      setCurrentUser(dummyFirebaseUser);

      // Use setTimeout to ensure state is set before navigation
      setTimeout(() => {
        // Check if there's a stored redirect URL (e.g., from clinic page)
        const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
        console.log('[OTP Login] Checking redirect URL:', redirectUrl);
        if (redirectUrl) {
          console.log('[OTP Login] Found redirect URL, navigating to:', redirectUrl);
          sessionStorage.removeItem('redirectAfterLogin');
          navigate(redirectUrl);
          return;
        }
        console.log('[OTP Login] No redirect URL found, going to dashboard');

        // Navigate to appropriate dashboard
        const activeRoles = userData.roles
          .filter(r => r.isActive)
          .map(r => r.role);

        const priorityOrder: UserRole[] = ['admin', 'examiner', 'manager', 'coordinator', 'marketer', 'audit', 'partner', 'volunteer', 'coach', 'customer'];
        const highestRole = priorityOrder.find(role => activeRoles.includes(role));

        if (highestRole) {
          const routePath = highestRole === 'coach' ? 'doctor' : highestRole;
          navigate(`/dashboard/${routePath}`);
        } else {
          navigate('/dashboard/customer');
        }
      }, 100);
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await logoutUser();
      setUser(null);
      setUserData(null);
      setCurrentUser(null);
      navigate('/login');
    } catch (error) {
      console.error('Failed to logout:', error);
      toast.error('Failed to logout. Please try again.');
    }
  };

  const value = {
    user,
    userData,
    loading,
    login,
    loginWithWhatsAppOTP,
    logout,
    setUserData,
    currentUser,
    hasRole,
    switchRole,
    navigate
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
} 